import React, { useState, useRef, useCallback } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function HydrostaticEquilibrium() {
  const [fusionRate, setFusionRate] = useState<number>(50);
  const currentRadiusRef = useRef<number>(90);

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const width = canvas.clientWidth;
    const height = canvas.clientHeight;

    const cx = width / 2 - 40; // Shift left slightly to make room for right-side labels
    const cy = height / 2;

    // Clear background
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // Calculate Target Radius: range 40px to 140px
    const targetRadius = 40 + (fusionRate / 100) * 100;
    // Smooth lerp
    currentRadiusRef.current += (targetRadius - currentRadiusRef.current) * 0.05;
    const currentRadius = currentRadiusRef.current;

    const isEquilibrium = fusionRate >= 45 && fusionRate <= 55;
    const isCollapsing = fusionRate < 45;
    const isExpanding = fusionRate > 55;

    // 1. Draw concentric layers (Photosphere -> Convection -> Radiation -> Core)
    // Gold/Yellow pulse if in equilibrium
    const pulseIntensity = isEquilibrium ? 0.08 * Math.sin(totalTime * 6) + 0.08 : 0;

    // Photosphere (Outermost)
    ctx.fillStyle = `rgba(255, 100, 30, ${0.18 + pulseIntensity})`;
    ctx.beginPath();
    ctx.arc(cx, cy, currentRadius, 0, Math.PI * 2);
    ctx.fill();

    if (isEquilibrium) {
      ctx.strokeStyle = `rgba(251, 191, 36, ${0.3 + pulseIntensity * 4})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(cx, cy, currentRadius, 0, Math.PI * 2);
      ctx.stroke();
    }

    // Convection Zone
    ctx.fillStyle = 'rgba(255, 150, 0, 0.25)';
    ctx.beginPath();
    ctx.arc(cx, cy, currentRadius * 0.75, 0, Math.PI * 2);
    ctx.fill();

    // Radiation Zone
    ctx.fillStyle = 'rgba(255, 200, 0, 0.4)';
    ctx.beginPath();
    ctx.arc(cx, cy, currentRadius * 0.5, 0, Math.PI * 2);
    ctx.fill();

    // Core (Innermost)
    const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, currentRadius * 0.25);
    coreGrad.addColorStop(0, '#FFFFFF');
    coreGrad.addColorStop(0.4, '#FDE047'); // Yellow yellow
    coreGrad.addColorStop(1, '#F59E0B'); // Darker yellow/orange edge
    ctx.fillStyle = coreGrad;
    ctx.beginPath();
    ctx.arc(cx, cy, currentRadius * 0.25, 0, Math.PI * 2);
    ctx.fill();

    // 2. Draw Scientific Label Callouts (to the right side)
    const drawCallout = (name: string, r: number, textY: number) => {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 3]);
      
      ctx.beginPath();
      ctx.moveTo(cx + r, cy);
      ctx.lineTo(cx + currentRadius + 30, textY);
      ctx.lineTo(cx + currentRadius + 85, textY);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(name, cx + currentRadius + 90, textY + 3);
    };

    drawCallout('Core', currentRadius * 0.25, cy - 54);
    drawCallout('Radiation Zone', currentRadius * 0.5, cy - 18);
    drawCallout('Convection Zone', currentRadius * 0.75, cy + 18);
    drawCallout('Photosphere', currentRadius, cy + 54);

    // 3. Draw Force Vectors (8 inward gravity arrows, 8 outward pressure arrows)
    const rotationAngle = totalTime * 0.2; // Slowly rotate

    for (let i = 0; i < 8; i++) {
      const angle = (i * Math.PI) / 4 + rotationAngle;

      // --- INWARD ARROWS (Gravity - Red/Gold) ---
      const inwardLength = isEquilibrium ? 20 : (isCollapsing ? 32 : 16);
      const inStartR = currentRadius + inwardLength + 6;
      const inEndR = currentRadius + 6;
      const inSx = cx + Math.cos(angle) * inStartR;
      const inSy = cy + Math.sin(angle) * inStartR;
      const inEx = cx + Math.cos(angle) * inEndR;
      const inEy = cy + Math.sin(angle) * inEndR;

      ctx.strokeStyle = isEquilibrium ? '#FBBF24' : '#F87171'; // Gold if balanced, red otherwise
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(inSx, inSy);
      ctx.lineTo(inEx, inEy);
      ctx.stroke();

      // Inward Arrowhead
      const inAngle = angle + Math.PI;
      ctx.fillStyle = isEquilibrium ? '#FBBF24' : '#F87171';
      ctx.beginPath();
      ctx.moveTo(inEx, inEy);
      ctx.lineTo(
        inEx - 5 * Math.cos(inAngle - Math.PI / 6),
        inEy - 5 * Math.sin(inAngle - Math.PI / 6)
      );
      ctx.lineTo(
        inEx - 5 * Math.cos(inAngle + Math.PI / 6),
        inEy - 5 * Math.sin(inAngle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();

      // --- OUTWARD ARROWS (Pressure - Blue/Gold) ---
      const outwardLength = isEquilibrium ? 20 : (isExpanding ? 32 : 12);
      const outStartR = currentRadius * 0.25;
      const outEndR = currentRadius * 0.25 + outwardLength;
      const outSx = cx + Math.cos(angle) * outStartR;
      const outSy = cy + Math.sin(angle) * outStartR;
      const outEx = cx + Math.cos(angle) * outEndR;
      const outEy = cy + Math.sin(angle) * outEndR;

      ctx.strokeStyle = isEquilibrium ? '#FBBF24' : '#60A5FA'; // Gold if balanced, blue otherwise
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(outSx, outSy);
      ctx.lineTo(outEx, outEy);
      ctx.stroke();

      // Outward Arrowhead
      const outAngle = angle;
      ctx.fillStyle = isEquilibrium ? '#FBBF24' : '#60A5FA';
      ctx.beginPath();
      ctx.moveTo(outEx, outEy);
      ctx.lineTo(
        outEx - 5 * Math.cos(outAngle - Math.PI / 6),
        outEy - 5 * Math.sin(outAngle - Math.PI / 6)
      );
      ctx.lineTo(
        outEx - 5 * Math.cos(outAngle + Math.PI / 6),
        outEy - 5 * Math.sin(outAngle + Math.PI / 6)
      );
      ctx.closePath();
      ctx.fill();
    }

    // 4. Draw HUD/Status Indicators
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (isEquilibrium) {
      ctx.fillStyle = '#22C55E';
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText('Equilibrium! (⚖ Balanced)', cx, cy);
    } else if (isCollapsing) {
      ctx.fillStyle = '#F87171';
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText('Collapsing! (💥 Gravity wins)', cx, cy);
    } else {
      ctx.fillStyle = '#60A5FA';
      ctx.font = 'bold 13px sans-serif';
      ctx.fillText('Expanding! (💨 Pressure wins)', cx, cy);
    }

    // Top-Left Simulation Info Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('Keplerian Hydrostatic Equilibrium Simulator', 12, 24);
  }, [fusionRate]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Slider overlaid at the bottom */}
      <div className="absolute bottom-3 left-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex flex-col gap-1.5 min-w-[250px]">
        <span className="text-white text-[10px] uppercase font-bold tracking-wider opacity-60">
          Core Fusion Rate: {Math.round(fusionRate)}%
        </span>
        <input
          type="range"
          min="0"
          max="100"
          value={fusionRate}
          onChange={(e) => setFusionRate(parseFloat(e.target.value))}
          className="w-full accent-cyan-400 cursor-pointer h-1 bg-gradient-to-r from-blue-500 to-red-500 rounded-lg appearance-none"
        />
      </div>
    </div>
  );
}
